import { useState, useEffect, useRef } from "react";
import { formatPrice, formatGoldPrice } from "../utils/helpers";
import HistoryPanel from "./HistoryPanel";
import PriceChart from "./PriceChart";

const LOGO_URL = "/qen.png";
const LOGO_URL_MOBILE = "/qen-mobile.png";
const LOGO_MASK_STYLE = {
  WebkitMaskImage: `url(${process.env.PUBLIC_URL}/qen-white.png)`,
  maskImage: `url(${process.env.PUBLIC_URL}/qen-white.png)`,
};

const MOBILE_LOGO_MASK_STYLE = {
  WebkitMaskImage: `url(${process.env.PUBLIC_URL}/qen-mobile.png)`,
  maskImage: `url(${process.env.PUBLIC_URL}/qen-mobile.png)`,
};

const ModernLayout = ({
  prices, gramPrices, hasAltin, sarrafiyePrices, aksoPrices, goldPrices,
  priceDirections, tickerSettings, profile, theme,
  isMobile, historyOpen, setHistoryOpen, now, tvMode
}) => {
  const getGramPrice = (gram) => gramPrices.find(p => p.gram === gram) || { qen_alis: null, qen_satis: null };
  const getSarrafiyePrice = (name) => sarrafiyePrices.find(p => p.name === name) || { qen_alis: null, qen_satis: null };
  // Ana profilde (default) gecmis veriler butonu kapali; subelerde acik
  const historyEnabled = profile !== "default";
  const openHistory = () => { if (historyEnabled) setHistoryOpen(true); };

  const [arrowTimestamps, setArrowTimestamps] = useState({});
  const prevDirsRef = useRef({});
  const [, forceUpdate] = useState(0);
  const [chartProduct, setChartProduct] = useState(null);
  
  useEffect(() => {
    const newTs = {};
    let changed = false;
    Object.entries(priceDirections).forEach(([k, v]) => {
      if (prevDirsRef.current[k] !== v) {
        newTs[k] = Date.now();
        changed = true;
      }
    });
    if (changed) setArrowTimestamps(prev => ({ ...prev, ...newTs }));
    prevDirsRef.current = { ...priceDirections };
  }, [priceDirections]);

  useEffect(() => {
    const timer = setInterval(() => forceUpdate(n => n + 1), 500);
    return () => clearInterval(timer);
  }, []);

  const Arrow = ({ dirKey }) => {
    const dir = priceDirections[dirKey];
    if (!dir) return null;
    const ts = arrowTimestamps[dirKey] || 0;
    const elapsed = Date.now() - ts;
    const opacity = elapsed > 3000 ? 0.35 : 1;
    return <span className={`price-arrow ${dir}`} style={{ opacity, transition: 'opacity 1s ease' }}>{dir === 'up' ? '▲' : '▼'}</span>;
  };

  const tickerItems = [
    { name: 'HAS ALTIN', buy: formatPrice(hasAltin?.qen_alis), sell: formatPrice(hasAltin?.qen_satis) },
    { name: 'ESKİ\u00A0\u00A0ÇEYREK', buy: formatPrice(getSarrafiyePrice('Çeyrek').qen_alis), sell: formatPrice(getSarrafiyePrice('Çeyrek').qen_satis) },
    { name: 'ESKİ\u00A0\u00A0YARIM', buy: formatPrice(getSarrafiyePrice('Yarım').qen_alis), sell: formatPrice(getSarrafiyePrice('Yarım').qen_satis) },
    { name: 'ESKİ\u00A0\u00A0TEKLİK', buy: formatPrice(getSarrafiyePrice('Teklik').qen_alis), sell: formatPrice(getSarrafiyePrice('Teklik').qen_satis) },
    { name: 'HURDA BİLEZİK (22)', buy: formatPrice(prices?.bilezik?.alis), sell: '-' },
  ];
  const tickerDouble = [...tickerItems, ...tickerItems, ...tickerItems];
  const isCustomTicker = tickerSettings.mode === 'custom' && tickerSettings.custom_text;
  const customTickerText = tickerSettings.custom_text || '';

  const renderTickerContent = () => {
    if (isCustomTicker) {
      const copies = Array(30).fill(customTickerText);
      return copies.map((text, i) => (
        <span className="modern-ticker-item modern-ticker-custom" key={i}>{text}</span>
      ));
    }
    return tickerDouble.map((t, i) => (
      <span className="modern-ticker-item" key={i}>
        <span className="t-name">{t.name}</span> <span>{t.buy}</span> <span className="t-sep">/</span> <span>{t.sell}</span>
      </span>
    ));
  };

  const dateParts = now.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const dayName = now.toLocaleDateString('tr-TR', { weekday: 'long' });
  const timeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  if (isMobile) {
    return (
      <>
        <div className="modern-mobile" data-testid="modern-mobile">
          <div className="modern-mobile-header">
            <span className="modern-mobile-logo-mask" style={{ ...MOBILE_LOGO_MASK_STYLE, cursor: historyEnabled ? 'pointer' : 'default' }} role={historyEnabled ? "button" : undefined} aria-label="QEN" data-testid="mobile-logo" onClick={openHistory} />
          </div>
          <div className="modern-mobile-ticker">
            <div className="modern-ticker-content">{renderTickerContent()}</div>
          </div>
          <div className="modern-mobile-panels">
            <div className="modern-panel">
              <div className="modern-panel-title"><span>GRAM ALTIN</span><span className="modern-panel-labels"><span>ALIŞ</span><span>SATIŞ</span></span></div>
              {['1', '5', '10', '20'].map(g => (
                <div className="modern-table-row" key={g} onClick={() => setChartProduct({ id: `gram_${g}`, label: `${g} GR Altın` })} style={{ cursor: 'pointer' }}>
                  <span className="modern-item-name">{g} GR <span className="modern-item-note">(24)</span></span>
                  <span className="modern-price">{formatPrice(getGramPrice(g).qen_alis)} <Arrow dirKey={`gram_${g}_alis`} /></span>
                  <span className="modern-price">{formatPrice(getGramPrice(g).qen_satis)} <Arrow dirKey={`gram_${g}_satis`} /></span>
                </div>
              ))}
            </div>
            <div className="modern-panel">
              <div className="modern-panel-title"><span>SARRAFİYE</span><span className="modern-panel-labels"><span>ALIŞ</span><span>SATIŞ</span></span></div>
              {[['Çeyrek','ESKİ\u00A0\u00A0ÇEYREK','ceyrek'],['Yarım','ESKİ\u00A0\u00A0YARIM','yarim'],['Teklik','ESKİ\u00A0\u00A0TEKLİK','teklik'],['Ata Lira','ESKİ\u00A0\u00A0ATA LİRA','ata_lira'],['Gremse','ESKİ\u00A0\u00A0GREMSE','gremse']].map(([key, label, chartId]) => (
                <div className="modern-table-row" key={key} onClick={() => setChartProduct({ id: chartId, label })} style={{ cursor: 'pointer' }}>
                  <span className="modern-item-name">{label}</span>
                  <span className="modern-price">{formatPrice(getSarrafiyePrice(key).qen_alis)} <Arrow dirKey={`sar_${key}_alis`} /></span>
                  <span className="modern-price">{formatPrice(getSarrafiyePrice(key).qen_satis)} <Arrow dirKey={`sar_${key}_satis`} /></span>
                </div>
              ))}
            </div>
            <div className="modern-mobile-bottom-card has-card" onClick={() => setChartProduct({ id: 'has_altin', label: 'HAS ALTIN' })} style={{ cursor: 'pointer' }}>
              <span className="modern-bottom-title">HAS ALTIN</span>
              <span className="modern-bottom-label">ALIŞ</span><span className="modern-bottom-label">SATIŞ</span>
              <span className="modern-bottom-price">{formatPrice(hasAltin?.qen_alis)} <Arrow dirKey="has_alis" /></span>
              <span className="modern-bottom-price">{formatPrice(hasAltin?.qen_satis)} <Arrow dirKey="has_satis" /></span>
            </div>
            <div className="modern-mobile-bottom-card" onClick={() => setChartProduct({ id: 'bilezik', label: 'HURDA BİLEZİK (22)' })} style={{ cursor: 'pointer' }}>
              <span className="modern-bottom-title">HURDA&nbsp;&nbsp;BİLEZİK <span className="modern-item-note">(22)</span></span>
              <span className="modern-bottom-label">ALIŞ</span>
              <span className="modern-bottom-price">{formatPrice(prices?.bilezik?.alis)} <Arrow dirKey="bilezik_alis" /></span>
            </div>
            <div className="modern-panel modern-akso-panel" style={{ margin: 0 }}>
              <div className="modern-panel-title"><span>ANKARA KUYUMCULAR ODASI FİYATLARI</span><span className="modern-akso-satis-head">SATIŞ</span></div>
              {[['ÇEYREK','ceyrek'],['YARIM','yarim'],['TEKLİK','teklik'],['ATA LİRA','ata'],['22 AYAR','ayar22']].map(([l, k]) => (
                <div className="modern-akso-row" key={k}><span className="modern-akso-name">{l}</span><span className="modern-akso-price">{formatPrice(aksoPrices?.[k]?.satis)}</span></div>
              ))}
              <div className="modern-akso-note">8-14-18-22 ayar urunlerde iscilik ilave edilmektedir.</div>
            </div>
            <div className="modern-panel gold-panel" style={{ margin: 0 }}>
              <div className="modern-panel-title"><span>GOLD</span><span className="modern-panel-labels"><span>ALIŞ</span><span>SATIŞ</span></span></div>
              <div className="modern-table-row" onClick={() => setChartProduct({ id: 'ons', label: 'ONS GOLD' })} style={{ cursor: 'pointer' }}>
                <span className="modern-item-name">ONS GOLD</span>
                <span className="modern-price">{formatGoldPrice(goldPrices?.ons?.alis)} <Arrow dirKey="ons_alis" /></span>
                <span className="modern-price">{formatGoldPrice(goldPrices?.ons?.satis)} <Arrow dirKey="ons_satis" /></span>
              </div>
            </div>
          </div>
          <div className="modern-copyright">Copyright &copy; QEN Özbek Kuyumculuk. Tüm hakları saklıdır.</div>
          <div className="modern-mobile-contact">
            <a href="tel:05063918181"><svg fill="currentColor" viewBox="0 0 24 24"><path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/></svg>DUTLUK</a>
            <a href="tel:05465802343"><svg fill="currentColor" viewBox="0 0 24 24"><path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/></svg>UFUKTEPE</a>
          </div>
          <HistoryPanel isOpen={historyOpen} onClose={() => setHistoryOpen(false)} profile={profile} theme={theme} />
          {chartProduct && <PriceChart product={chartProduct.id} label={chartProduct.label} onClose={() => setChartProduct(null)} profile={profile} theme={theme} />}
        </div>
      </>
    );
  }

  // MODERN DESKTOP
  return (
    <>
      <div className="modern-page" data-testid="modern-desktop">
        <div className="modern-header">
          <div className="modern-header-left">
            <span className="modern-logo modern-logo-mask" style={{ ...LOGO_MASK_STYLE, cursor: historyEnabled ? 'pointer' : 'default' }} role={historyEnabled ? "button" : undefined} aria-label="QEN" data-testid="modern-logo" onClick={openHistory} />
          </div>
          <div className="modern-header-center">
            <span className="modern-title">ÖZBEK KUYUMCULUK</span>
          </div>
          <div className="modern-header-right">
            <div className="modern-clock">
              <span className="time-text">{timeStr}</span>
              <div className="modern-date-row">
                <span className="date-text">{dayName.toUpperCase()}</span>
                <span className="date-text">{dateParts.toUpperCase()}</span>
              </div>
            </div>
            <div className="modern-phone-row">
              <span className="modern-phone-label">DUTLUK</span>
              <span className="modern-phone-number">0 506 391 81 81</span>
            </div>
            <div className="modern-phone-row">
              <span className="modern-phone-label">UFUKTEPE</span>
              <span className="modern-phone-number">0 546 580 23 43</span>
            </div>
          </div>
        </div>
        <div className="modern-ticker">
          <div className="modern-ticker-content">{renderTickerContent()}</div>
        </div>
        <div className="modern-main">
          <div className="modern-panel">
            <div className="modern-panel-title"><span>GRAM ALTIN</span><span className="modern-panel-labels"><span>ALIŞ</span><span>SATIŞ</span></span></div>
            {['1', '5', '10', '20'].map(g => (
              <div className="modern-table-row" key={g} onClick={() => setChartProduct({ id: `gram_${g}`, label: `${g} GR Altın` })} style={{ cursor: 'pointer' }}>
                <span className="modern-item-name">{g} GR <span className="modern-item-note">(24 Ayar)</span></span>
                <span className="modern-price">{formatPrice(getGramPrice(g).qen_alis)} <Arrow dirKey={`gram_${g}_alis`} /></span>
                <span className="modern-price">{formatPrice(getGramPrice(g).qen_satis)} <Arrow dirKey={`gram_${g}_satis`} /></span>
              </div>
            ))}
          </div>
          <div className="modern-panel">
            <div className="modern-panel-title"><span>SARRAFİYE</span><span className="modern-panel-labels"><span>ALIŞ</span><span>SATIŞ</span></span></div>
            {[['Çeyrek','ESKİ\u00A0\u00A0ÇEYREK','ceyrek'],['Yarım','ESKİ\u00A0\u00A0YARIM','yarim'],['Teklik','ESKİ\u00A0\u00A0TEKLİK','teklik'],['Ata Lira','ESKİ\u00A0\u00A0ATA LİRA','ata_lira'],['Gremse','ESKİ\u00A0\u00A0GREMSE','gremse']].map(([key, label, chartId]) => (
              <div className="modern-table-row" key={key} onClick={() => setChartProduct({ id: chartId, label })} style={{ cursor: 'pointer' }}>
                <span className="modern-item-name">{label}</span>
                <span className="modern-price">{formatPrice(getSarrafiyePrice(key).qen_alis)} <Arrow dirKey={`sar_${key}_alis`} /></span>
                <span className="modern-price">{formatPrice(getSarrafiyePrice(key).qen_satis)} <Arrow dirKey={`sar_${key}_satis`} /></span>
              </div>
            ))}
          </div>
        </div>
        <div className="modern-bottom-row">
          <div className="modern-bottom-card has-card" onClick={() => setChartProduct({ id: 'has_altin', label: 'HAS ALTIN' })} style={{ cursor: 'pointer' }}>
            <span className="modern-bottom-title">HAS ALTIN</span>
            <span className="modern-bottom-label">ALIŞ</span><span className="modern-bottom-label">SATIŞ</span>
            <span className="modern-bottom-price">{formatPrice(hasAltin?.qen_alis)} <Arrow dirKey="has_alis" /></span>
            <span className="modern-bottom-price">{formatPrice(hasAltin?.qen_satis)} <Arrow dirKey="has_satis" /></span>
          </div>
          <div className="modern-bottom-card" onClick={() => setChartProduct({ id: 'bilezik', label: 'HURDA BİLEZİK (22)' })} style={{ cursor: 'pointer' }} data-testid="22ayar-chart-btn">
            <span className="modern-bottom-title">HURDA&nbsp;&nbsp;BİLEZİK <span className="modern-item-note">(22 Ayar)</span></span>
            <span className="modern-bottom-label">ALIŞ</span>
            <span className="modern-bottom-price">{formatPrice(prices?.bilezik?.alis)} <Arrow dirKey="bilezik_alis" /></span>
          </div>
        </div>
        <div className="tv-spacer" style={{height: '400px'}}></div>
        {/* AKSO Section */}
        <div className="modern-panel modern-akso-panel">
          <div className="modern-panel-title"><span>ANKARA KUYUMCULAR ODASI FİYATLARI</span><span className="modern-akso-satis-head">SATIŞ</span></div>
          {[['ÇEYREK','ceyrek'],['YARIM','yarim'],['TEKLİK','teklik'],['ATA LİRA','ata'],['22 AYAR','ayar22']].map(([label, key]) => (
            <div className="modern-akso-row" key={key}><span className="modern-akso-name">{label}</span><span className="modern-akso-price">{formatPrice(aksoPrices?.[key]?.satis)}</span></div>
          ))}
          <div className="modern-akso-note">8-14-18-22 ayar urunlerde kuyumcular tarafindan iscilik ilave edilmektedir.</div>
        </div>
        <div className="tv-spacer" style={{height: '400px'}}></div>
        {/* GOLD Section */}
        <div className="modern-panel gold-panel" data-testid="gold-section" style={{ margin: '0 20px' }}>
          <div className="modern-panel-title"><span>GOLD</span><span className="modern-panel-labels"><span>ALIŞ</span><span>SATIŞ</span></span></div>
          <div className="modern-table-row" onClick={() => setChartProduct({ id: 'ons', label: 'ONS GOLD' })} style={{ cursor: 'pointer' }} data-testid="ons-gold-chart-btn">
            <span className="modern-item-name">ONS GOLD</span>
            <span className="modern-price">{formatGoldPrice(goldPrices?.ons?.alis)} <Arrow dirKey="ons_alis" /></span>
            <span className="modern-price">{formatGoldPrice(goldPrices?.ons?.satis)} <Arrow dirKey="ons_satis" /></span>
          </div>
        </div>
        <div className="modern-copyright">Copyright &copy; QEN Özbek Kuyumculuk. Tüm hakları saklıdır.</div>
        <HistoryPanel isOpen={historyOpen} onClose={() => setHistoryOpen(false)} profile={profile} theme={theme} />
        {chartProduct && <PriceChart product={chartProduct.id} label={chartProduct.label} onClose={() => setChartProduct(null)} profile={profile} theme={theme} />}
      </div>
    </>
  );
};

export default ModernLayout;
